import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useLatest } from 'react-use'
import { Vector3Tuple } from 'three'
import { PointShapesMesh, toImmutable } from '@modugen/ui-toolkit'
import { OpacityOption } from '@modugen/ui-toolkit/dist/scene/types/opacity'
import { ThreeEvent } from '@react-three/fiber'
import { useAppStore } from '@stores'
import { ElementLabelDescriptor } from '../ModelElementLabels/types'
import BatchedLabelText, { BatchedLabelTextItem, BatchedLabelTextSize } from './BatchedLabelText'
import {
  LABEL_BACKGROUND_POLYGON_OFFSET,
  LABEL_BACKGROUND_RENDER_ORDER,
  LABEL_BORDER_THICKNESS_PX,
  LABEL_OCCLUSION_DEPTH_STEP,
} from './constants'
import {
  getLabelPlanExtent,
  getLabelRectanglePoints,
  getLabelTextBlock,
  getPlaneNormal,
  LabelPlanExtent,
  quaternionFromBasis,
} from './utils'

type LabelRenderMode = {
  bodyGeometry: boolean
  outline: boolean
}

/** Suffix of the mirrored text of a label that reads from both sides */
const BACK_FACE_SUFFIX = '--back'

interface Props {
  descriptors: ElementLabelDescriptor[]
  /**
   * Make labels hide each other where they overlap, the way the per-label
   * meshes do, instead of blending together.
   *
   * Only usable where every label is fully opaque, and it staggers the labels
   * in depth (see `LABEL_OCCLUSION_DEPTH_STEP`), so it suits the top down
   * floorplan rather than the perspective 3D view.
   */
  occlude?: boolean
  /**
   * Click handler for label backgrounds; providing it enables raycasting on
   * them. Clicks on labels outside `clickableLabelGuids` are ignored and fall
   * through to the elements beneath. Clickable labels drawn without a
   * background get an invisible box of the same size to click instead.
   */
  onLabelClick?: (guid: string) => void
  /** Labels (by guid) that accept clicks; the rest stay click-transparent. */
  clickableLabelGuids?: ReadonlySet<string>
  /**
   * Called with the measured plan footprint of every label (by guid) whenever
   * the labels have been typeset, for UI that needs to sit next to a label
   * rather than on top of it. Must be memoized - it feeds the batch rebuild.
   */
  onLabelExtentsMeasured?: (extents: Record<string, LabelPlanExtent>) => void
}

/**
 * Draws any set of element labels with two batched draw calls: one for all the
 * text (troika's `BatchedText`) and one for all backgrounds and borders (the
 * toolkit's `PointShapesMesh`), instead of a handful of meshes per label.
 *
 * Trade-off versus the non-batched renderers: the text is flat SDF text rather
 * than extruded 3D geometry or a canvas sprite, and borders have a screen-space
 * rather than a world-space thickness.
 */
const BatchedElementLabels = ({
  descriptors,
  occlude = false,
  onLabelClick,
  clickableLabelGuids,
  onLabelExtentsMeasured,
}: Props): ReactElement => {
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)

  const [textSizes, setTextSizes] = useState<Record<string, BatchedLabelTextSize>>({})

  // Coplanar labels cannot occlude each other, so give each one its own depth
  // slice. Purely a render detail, the descriptors keep their true positions.
  const placed = useMemo(() => {
    if (!occlude) return descriptors

    return descriptors.map((descriptor, index) => ({
      ...descriptor,
      position: descriptor.position.add(
        toImmutable(
          getPlaneNormal(descriptor.xDirection, descriptor.yDirection).multiplyScalar(
            index * LABEL_OCCLUSION_DEPTH_STEP,
          ),
        ),
      ),
    }))
  }, [descriptors, occlude])

  const textItems = useMemo(
    () =>
      placed.reduce((collector: BatchedLabelTextItem[], descriptor) => {
        const {
          guid,
          textLines,
          position,
          xDirection,
          yDirection,
          fontSize,
          lineHeight,
          color,
          opacity,
          textDepth,
          extrudedThroughElement,
        } = descriptor

        const text = textLines.join('\n')

        collector.push({
          id: guid,
          text,
          fontSize,
          lineHeight,
          color,
          fillOpacity: opacity,
          position: position.v,
          quaternion: quaternionFromBasis(xDirection, yDirection),
        })

        // The non-batched variant extrudes these labels all the way through the
        // element, which makes them readable from both sides. Flat text needs a
        // mirrored copy on the far face to match.
        if (extrudedThroughElement) {
          collector.push({
            id: `${guid}${BACK_FACE_SUFFIX}`,
            text,
            fontSize,
            lineHeight,
            color,
            fillOpacity: opacity,
            position: position.v
              .clone()
              .addScaledVector(getPlaneNormal(xDirection, yDirection), -textDepth),
            quaternion: quaternionFromBasis(xDirection.clone().negate(), yDirection),
          })
        }

        return collector
      }, []),
    [placed],
  )

  // The descriptors the sizes belong to; read at measure time rather than
  // closed over, so a descriptor change does not change `onMeasured` (which
  // would rebuild the batch a second time).
  const latestDescriptors = useLatest(descriptors)

  const onMeasured = useCallback(
    (sizes: BatchedLabelTextSize[]) => {
      const sizeById = Object.fromEntries(sizes.map(size => [size.id, size]))
      setTextSizes(previous => ({ ...previous, ...sizeById }))

      if (!onLabelExtentsMeasured) return
      const extents: Record<string, LabelPlanExtent> = {}
      for (const descriptor of latestDescriptors.current) {
        const size = sizeById[descriptor.guid]
        if (size && getLabelTextBlock(descriptor, size).width) {
          extents[descriptor.guid] = getLabelPlanExtent(descriptor, size)
        }
      }
      onLabelExtentsMeasured(extents)
    },
    [onLabelExtentsMeasured, latestDescriptors],
  )

  const backgrounds = useMemo(
    () =>
      placed.reduce(
        (
          collector: {
            elements: { id: string; points: Vector3Tuple[] }[]
            guidToColor: Record<string, string>
            guidToOutlinesColor: Record<string, string>
            guidToOpacity: Record<string, OpacityOption>
            guidToRenderMode: Record<string, LabelRenderMode>
          },
          descriptor,
        ) => {
          const { guid, opacity, backgroundColor, borderColor } = descriptor
          const size = textSizes[guid]
          if (!backgroundColor || !size || !getLabelTextBlock(descriptor, size).width) {
            return collector
          }

          collector.elements.push({ id: guid, points: getLabelRectanglePoints(descriptor, size) })
          collector.guidToColor[guid] = backgroundColor
          collector.guidToOutlinesColor[guid] = borderColor || backgroundColor
          collector.guidToOpacity[guid] = opacity < 1 ? 'transparent' : 'notTransparent'
          collector.guidToRenderMode[guid] = { bodyGeometry: true, outline: !!borderColor }

          return collector
        },
        {
          elements: [],
          guidToColor: {},
          guidToOutlinesColor: {},
          guidToOpacity: {},
          guidToRenderMode: {},
        },
      ),
    [placed, textSizes],
  )

  // Clickable labels without a background (e.g. the stiffening plan's segment
  // labels, borderless and background-less in the PDF) still need something to
  // hit: the same box, raycast but never drawn, so the scene keeps matching the
  // PDF.
  const hitAreas = useMemo(
    () =>
      !onLabelClick
        ? []
        : placed.reduce((collector: { id: string; points: Vector3Tuple[] }[], descriptor) => {
            const { guid, backgroundColor } = descriptor
            const size = textSizes[guid]
            if (backgroundColor || !clickableLabelGuids?.has(guid)) return collector
            if (!size || !getLabelTextBlock(descriptor, size).width) return collector
            collector.push({ id: guid, points: getLabelRectanglePoints(descriptor, size) })
            return collector
          }, []),
    [onLabelClick, placed, textSizes, clickableLabelGuids],
  )

  const meshBasicMaterialProps = useMemo(
    () => ({
      // the backgrounds have to write depth too, otherwise they cannot hide the
      // text of the labels stacked behind them
      depthWrite: occlude,
      // At full opacity `PointShapesMesh` leaves the material opaque, which puts
      // the background in the opaque queue - drawn before the translucent
      // element bodies, which then blend over it. Forcing it transparent moves it
      // into the same queue as those bodies, where its `renderOrder` puts it on
      // top. In occluding mode the labels are opaque and rely on the depth
      // buffer instead, so leave that path alone.
      ...(occlude ? {} : { transparent: true }),
      polygonOffset: true,
      polygonOffsetFactor: LABEL_BACKGROUND_POLYGON_OFFSET,
      polygonOffsetUnits: LABEL_BACKGROUND_POLYGON_OFFSET,
    }),
    [occlude],
  )

  const opacityOptionValues = useMemo<Record<OpacityOption, number>>(
    () => ({ transparent: transparentOpacity, notTransparent: 1 }),
    [transparentOpacity],
  )

  // Not clickable -> no stopPropagation, so the click falls through to the
  // element underneath the label.
  const handleBackgroundClick = useCallback(
    (elementId: string, event: ThreeEvent<MouseEvent>) => {
      if (!onLabelClick || !clickableLabelGuids?.has(elementId)) return
      event.stopPropagation()
      onLabelClick(elementId)
    },
    [onLabelClick, clickableLabelGuids],
  )

  // `visible: false` skips drawing while three's raycast still hits the boxes.
  const hitAreaMaterialProps = useMemo(() => ({ visible: false, depthWrite: false }), [])
  // The material only covers the box body; its outline is drawn separately.
  const hitAreaRenderModes = useMemo(
    () =>
      Object.fromEntries<LabelRenderMode>(
        hitAreas.map(({ id }) => [id, { bodyGeometry: true, outline: false }]),
      ),
    [hitAreas],
  )

  // Only the clickable labels should show a pointer cursor.
  const guidToHoverable = useMemo(() => {
    if (!onLabelClick) return undefined
    return Object.fromEntries(
      backgrounds.elements.map(element => [element.id, !!clickableLabelGuids?.has(element.id)]),
    )
  }, [onLabelClick, backgrounds.elements, clickableLabelGuids])

  return (
    <>
      {backgrounds.elements.length > 0 && (
        <PointShapesMesh
          elements={backgrounds.elements}
          guidToColor={backgrounds.guidToColor}
          guidToOutlinesColor={backgrounds.guidToOutlinesColor}
          guidToOpacity={backgrounds.guidToOpacity}
          guidToRenderMode={backgrounds.guidToRenderMode}
          opacityOptionValues={opacityOptionValues}
          meshBasicMaterialProps={meshBasicMaterialProps}
          outlineThickness={LABEL_BORDER_THICKNESS_PX}
          renderOrder={LABEL_BACKGROUND_RENDER_ORDER}
          raycastingEnabled={!!onLabelClick}
          handleClick={handleBackgroundClick}
          guidToHoverable={guidToHoverable}
        />
      )}

      {hitAreas.length > 0 && (
        <PointShapesMesh
          elements={hitAreas}
          meshBasicMaterialProps={hitAreaMaterialProps}
          guidToRenderMode={hitAreaRenderModes}
          raycastingEnabled
          handleClick={handleBackgroundClick}
        />
      )}

      {/* an empty batch would still allocate a geometry, a shader and the SDF atlas */}
      {textItems.length > 0 && (
        <BatchedLabelText items={textItems} occlude={occlude} onMeasured={onMeasured} />
      )}
    </>
  )
}

export default BatchedElementLabels
